import { useCallback, useEffect, useMemo, useState } from "react";
import { request } from "../../../../utils/api";
import { resolveUserRoleName } from "../../../../constants";

const CACHE_TTL_MS = 60_000;
let cache = { loadedAt: 0, employees: [] };

export const employeeDisplayName = (user) =>
  `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
  user?.name ||
  user?.userName ||
  "";

export const employeeRoleName = (user) =>
  resolveUserRoleName(user) || user?.roleName || "";

const toPickerUser = (row) => ({
  id: Number(row.id),
  firstName: row.firstName || "",
  lastName: row.lastName || "",
  roleName: row.roleName || row.role?.name || "",
  role: { name: row.roleName || row.role?.name || "" },
});

const parseEmployeeList = (response) => {
  const rows =
    response?.result?.employees ||
    response?.result?.users ||
    response?.employees ||
    [];
  return Array.isArray(rows) ? rows.map(toPickerUser).filter((user) => user.id) : [];
};

/**
 * Active tenant employees for Admin/OA pickers (module cache, 60s).
 * Uses the Daily Log options endpoint so OA can load the list without full user-admin access.
 */
export const useActiveEmployees = (enabled = true) => {
  const [employees, setEmployees] = useState(cache.employees);
  const [loading, setLoading] = useState(
    enabled && Date.now() - cache.loadedAt > CACHE_TTL_MS,
  );

  const load = useCallback(async (force = false) => {
    if (!enabled) return;
    const fresh = Date.now() - cache.loadedAt < CACHE_TTL_MS;
    if (!force && fresh && cache.employees.length) {
      setEmployees(cache.employees);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await request("/daily-logs/employees");
      const list = parseEmployeeList(response);
      cache = { loadedAt: Date.now(), employees: list };
      setEmployees(list);
    } catch {
      setEmployees(cache.employees);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    return undefined;
  }, [enabled, load]);

  const byId = useMemo(() => {
    const map = new Map();
    employees.forEach((user) => map.set(Number(user.id), user));
    return map;
  }, [employees]);

  return { employees, loading, byId, reload: () => load(true) };
};
